import axios from 'axios'
import {
  postPurchaseOrder,
  postPurchaseOrderItem,
  purchaseOrder,
  purchaseOrderGrid,
  purchaseOrderItem,
  purchaseOrderJobDefaults,
  purchaseOrderMatch,
  updatePurchaseOrder,
} from './interfaces'

const apiURL = process.env.REACT_APP_API_URL

const authHeaders = () => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  return {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + token,
  }
}

export interface purchaseOrderFilters {
  showDeleted?: boolean
  showDrafts?: boolean
  month?: number | null
  jobId?: number | null
  supplierId?: number | null
  clientId?: number | null
}

// GET: /PurchaseOrder
export const getPurchaseOrders = async (
  filters: purchaseOrderFilters = {}
): Promise<purchaseOrderGrid[]> => {
  const params = new URLSearchParams()

  params.append('showDeleted', String(filters.showDeleted ?? false))
  params.append('showDrafts', String(filters.showDrafts ?? true))

  if (filters.month) params.append('month', String(filters.month))
  if (filters.jobId) params.append('jobId', String(filters.jobId))
  if (filters.supplierId) params.append('supplierId', String(filters.supplierId))
  if (filters.clientId) params.append('clientId', String(filters.clientId))

  const response = await axios.get<purchaseOrderGrid[]>(
    `https://${apiURL}/PurchaseOrder?${params.toString()}`,
    { headers: authHeaders() }
  )

  return response.data
}

// GET: /PurchaseOrder/{id}
export const getPurchaseOrder = async (purchaseOrderId: number): Promise<purchaseOrder> => {
  const response = await axios.get<purchaseOrder>(
    `https://${apiURL}/PurchaseOrder/${purchaseOrderId}`,
    { headers: authHeaders() }
  )

  return response.data
}

// GET: /PurchaseOrder/Job/{jobId}
export const getPurchaseOrdersByJob = async (jobId: number): Promise<purchaseOrder[]> => {
  const response = await axios.get<purchaseOrder[]>(
    `https://${apiURL}/PurchaseOrder/Job/${jobId}`,
    { headers: authHeaders() }
  )

  return response.data
}

// GET: /PurchaseOrder/JobDefaults/{jobId}
export const getPurchaseOrderJobDefaults = async (
  jobId: number
): Promise<purchaseOrderJobDefaults> => {
  const response = await axios.get<purchaseOrderJobDefaults>(
    `https://${apiURL}/PurchaseOrder/JobDefaults/${jobId}`,
    { headers: authHeaders() }
  )

  return response.data
}

// GET: /PurchaseOrder/{id}/Items
export const getPurchaseOrderItems = async (
  purchaseOrderId: number
): Promise<purchaseOrderItem[]> => {
  const response = await axios.get<purchaseOrderItem[]>(
    `https://${apiURL}/PurchaseOrder/${purchaseOrderId}/Items`,
    { headers: authHeaders() }
  )

  return response.data
}

// POST: /PurchaseOrder
export const createPurchaseOrder = async (order: postPurchaseOrder): Promise<purchaseOrder> => {
  const response = await axios.post<purchaseOrder>(
    `https://${apiURL}/PurchaseOrder`,
    order,
    { headers: authHeaders() }
  )

  return response.data
}

// PUT: /PurchaseOrder/{id}
export const savePurchaseOrder = async (order: updatePurchaseOrder): Promise<purchaseOrder> => {
  const response = await axios.put<purchaseOrder>(
    `https://${apiURL}/PurchaseOrder/${order.purchaseOrderId}`,
    order,
    { headers: authHeaders() }
  )

  return response.data
}

// POST: /PurchaseOrder/{id}/Publish - commits a draft and stamps its order number
export const publishPurchaseOrder = async (purchaseOrderId: number): Promise<purchaseOrder> => {
  const response = await axios.post<purchaseOrder>(
    `https://${apiURL}/PurchaseOrder/${purchaseOrderId}/Publish`,
    {},
    { headers: authHeaders() }
  )

  return response.data
}

// POST: /PurchaseOrder/Item/{id}/Delivered
// Sets how many of one line have arrived in total; 0 undoes it. Returns the
// whole order, since its own delivered status may change with the line.
export const setPurchaseOrderItemDelivered = async (
  purchaseOrderItemId: number,
  quantityDelivered: number
): Promise<purchaseOrder> => {
  const response = await axios.post<purchaseOrder>(
    `https://${apiURL}/PurchaseOrder/Item/${purchaseOrderItemId}/Delivered`,
    { quantityDelivered },
    { headers: authHeaders() }
  )

  return response.data
}

// POST: /PurchaseOrder/{id}/Delivered
// Marks every line of a published order as delivered in full; pass false to
// clear every line instead.
export const setPurchaseOrderDelivered = async (
  purchaseOrderId: number,
  delivered: boolean
): Promise<purchaseOrder> => {
  const response = await axios.post<purchaseOrder>(
    `https://${apiURL}/PurchaseOrder/${purchaseOrderId}/Delivered?delivered=${delivered}`,
    {},
    { headers: authHeaders() }
  )

  return response.data
}

// DELETE: /PurchaseOrder/{id}
export const deletePurchaseOrder = async (purchaseOrderId: number): Promise<void> => {
  await axios.delete(`https://${apiURL}/PurchaseOrder/${purchaseOrderId}`, {
    headers: authHeaders(),
  })
}

// POST: /PurchaseOrder/Item
export const addPurchaseOrderItem = async (
  purchaseOrderId: number,
  item: postPurchaseOrderItem
): Promise<purchaseOrderItem> => {
  const response = await axios.post<purchaseOrderItem>(
    `https://${apiURL}/PurchaseOrder/Item`,
    { purchaseOrderId, ...item },
    { headers: authHeaders() }
  )

  return response.data
}

// PUT: /PurchaseOrder/Item/{id}
export const updatePurchaseOrderItem = async (
  purchaseOrderItemId: number,
  item: postPurchaseOrderItem
): Promise<purchaseOrderItem> => {
  const response = await axios.put<purchaseOrderItem>(
    `https://${apiURL}/PurchaseOrder/Item/${purchaseOrderItemId}`,
    { purchaseOrderItemId, ...item },
    { headers: authHeaders() }
  )

  return response.data
}

// DELETE: /PurchaseOrder/Item/{id}
export const deletePurchaseOrderItem = async (purchaseOrderItemId: number): Promise<void> => {
  await axios.delete(`https://${apiURL}/PurchaseOrder/Item/${purchaseOrderItemId}`, {
    headers: authHeaders(),
  })
}

// GET: /PurchaseOrder/Matches - one row per live piece, for the grid indicator
export const getPurchaseOrderMatches = async (): Promise<purchaseOrderMatch[]> => {
  const response = await axios.get<purchaseOrderMatch[]>(
    `https://${apiURL}/PurchaseOrder/Matches`,
    { headers: authHeaders() }
  )

  return response.data
}

// GET: /PurchaseOrder/Match/Pieces/{piecesId}
export const getPurchaseOrderMatchForPiece = async (
  piecesId: number
): Promise<purchaseOrderMatch> => {
  const response = await axios.get<purchaseOrderMatch>(
    `https://${apiURL}/PurchaseOrder/Match/Pieces/${piecesId}`,
    { headers: authHeaders() }
  )

  return response.data
}

// GET: /PurchaseOrder/Allocatable - published orders free to be allocated,
// plus whichever one is already on this piece.
export const getAllocatablePurchaseOrders = async (
  piecesId?: number
): Promise<purchaseOrder[]> => {
  const query = piecesId ? `?piecesId=${piecesId}` : ''

  const response = await axios.get<purchaseOrder[]>(
    `https://${apiURL}/PurchaseOrder/Allocatable${query}`,
    { headers: authHeaders() }
  )

  return response.data
}

// POST: /PurchaseOrder/{id}/Allocate/{piecesId}
export const allocatePurchaseOrder = async (
  purchaseOrderId: number,
  piecesId: number
): Promise<purchaseOrder> => {
  const response = await axios.post<purchaseOrder>(
    `https://${apiURL}/PurchaseOrder/${purchaseOrderId}/Allocate/${piecesId}`,
    {},
    { headers: authHeaders() }
  )

  return response.data
}

// DELETE: /PurchaseOrder/{id}/Allocate
export const deallocatePurchaseOrder = async (purchaseOrderId: number): Promise<purchaseOrder> => {
  const response = await axios.delete<purchaseOrder>(
    `https://${apiURL}/PurchaseOrder/${purchaseOrderId}/Allocate`,
    { headers: authHeaders() }
  )

  return response.data
}

/**
 * The server's message for a failed request whose body came back as a Blob.
 * responseType 'blob' applies to error responses too, so the plain text the API
 * returns for e.g. an unpublished order is not readable as error.response.data.
 */
const readBlobError = async (error: any, fallback: string): Promise<string> => {
  const data = error?.response?.data

  if (data instanceof Blob) {
    const text = await data.text()
    return text || fallback
  }

  return data?.message || error?.message || fallback
}

/**
 * Generates the purchase order PDF and opens it in a new window. Used from both
 * the Purchase Orders section and a job's purchase order tab, which share the
 * same form.
 */
export const openPurchaseOrderPdf = async (purchaseOrderId: number): Promise<void> => {
  // Opened before the request, while the click is still the active user
  // gesture - a window.open after the await is what a popup blocker stops.
  const pdfWindow = window.open('', '_blank')

  try {
    const response = await axios.get(
      `https://${apiURL}/PDF/PurchaseOrder/${purchaseOrderId}`,
      {
        responseType: 'blob',
        headers: authHeaders(),
      }
    )

    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: 'application/pdf' })
    )

    if (pdfWindow) {
      pdfWindow.location.href = url
      return
    }

    // Popup blocked, so fall back to a download rather than losing the PDF the
    // user has already waited for.
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `purchaseorder-${purchaseOrderId}.pdf`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error: any) {
    pdfWindow?.close()
    throw new Error(await readBlobError(error, 'Failed to generate the purchase order PDF'))
  }
}
